import { createSlice } from "@reduxjs/toolkit";
import { TreeData, ElpReportData } from "@src/components/tree/TreeTypes";

interface InitialState {
  parentTree: TreeData[];
  pageTree: TreeData[];
  selectedNode: TreeData | null;
  newSelectedNode: TreeData | null;
  tempParentTree: TreeData[];
  tempPageTree: TreeData[];
  syllabusTree: TreeData[];
  tempSyllabusTree: TreeData[];
  selectedNodeName: string;
  elpReportData: Map<string, ElpReportData>;
  elpReportLoading: boolean;
  elpReportError: string | null;
}

const initialState: InitialState = {
  parentTree: [],
  pageTree: [],
  selectedNode: null,
  newSelectedNode: null,
  tempParentTree: [],
  tempPageTree: [],
  syllabusTree: [],
  tempSyllabusTree: [],
  selectedNodeName: "",
  elpReportData: new Map(),
  elpReportLoading: false,
  elpReportError: null,
};

// Helper to recursively add children
const addChildrenRecursively = (
  tree: TreeData[],
  parentId: string,
  children: TreeData[]
): TreeData[] => {
  return tree.map((node) => {
    if (node._id === parentId) {
      return {
        ...node,
        expanded: node.hasChildren ? true : false,
        children,
      };
    } else if (node.children) {
      return {
        ...node,
        children: addChildrenRecursively(node.children, parentId, children),
      };
    }
    return node;
  });
};

// Helper to toggle expanded/collapse state
const toggleExpandedById = (
  arr: TreeData[] | undefined,
  parentId: string | null
): TreeData[] => {
  return arr
    ? arr.map((item) => {
        if (Array.isArray(item.children)) {
          const updatedItem = {
            ...item,
            expanded: item._id === parentId ? !item.expanded : item.expanded,
            children: toggleExpandedById(
              item.children,
              item._id === parentId && !item.expanded ? null : parentId
            ),
          };

          // If parent is collapsed, collapse all children
          if (item._id === parentId && !updatedItem.expanded) {
            updatedItem.children = updatedItem.children.map((child) => ({
              ...child,
              expanded: false,
              children: toggleExpandedById(child.children, null),
            }));
          }

          return updatedItem;
        }
        return item;
      })
    : [];
};

// Convert plain object to Map
const convertToMap = (data: any): Map<string, ElpReportData> => {
  const map = new Map<string, ElpReportData>();
  if (data && typeof data === "object") {
    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === "object" && "parentId" in (value as any)) {
        map.set(key, value as ElpReportData);
      }
    });
  }
  return map;
};

const elpSelectTreeSlice = createSlice({
  name: "elpSelectTree",
  initialState,
  reducers: {
    setPageTreeData: (state, action) => {
      state.pageTree = action.payload;
    },
    setTempPageTreeData: (state, action) => {
      state.tempPageTree = action.payload;
    },
    setParentTreeData: (state, action) => {
      state.parentTree = action.payload;
    },
    setTempParentTreeData: (state, action) => {
      state.tempParentTree = action.payload;
    },
    setSyllabusTreeData: (state, action) => {
      state.syllabusTree = action.payload;
    },
    setTempSyllabusTreeData: (state, action) => {
      state.tempSyllabusTree = action.payload;
    },
    updateSelectedParent: (state, action) => {
      state.parentTree = action.payload;
      state.tempParentTree = action.payload;
    },
    setSelectedNode: (state, action) => {
      state.selectedNode = action.payload;
    },
    setNewSelectedNode: (state, action) => {
      state.newSelectedNode = action.payload;
    },
    addChildrenToPageTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.pageTree = addChildrenRecursively(
        state.pageTree,
        parentId,
        children
      );
      state.tempPageTree = addChildrenRecursively(
        state.pageTree,
        parentId,
        children
      );
    },
    addChildrenToParentTree: (state, action) => {
      const { parentId, children } = action.payload;
      state.parentTree = addChildrenRecursively(
        state.parentTree,
        parentId,
        children
      );
      state.tempParentTree = addChildrenRecursively(
        state.parentTree,
        parentId,
        children
      );
    },
    collapseExpandInPageTree: (state, action) => {
      state.pageTree = toggleExpandedById(state.pageTree, action.payload._id);
    },
    collapseExpandInParentTree: (state, action) => {
      state.parentTree = toggleExpandedById(
        state.parentTree,
        action.payload._id
      );
    },
    collapseExpandInSyllabusTree: (state, action) => {
      state.syllabusTree = toggleExpandedById(
        state.syllabusTree,
        action.payload._id
      );
    },
    setSelectedNodeName: (state, action) => {
      state.selectedNodeName = action.payload;
    },
    // ELP Report Reducers
    setElpReportData: (state, action) => {
      if (action.payload instanceof Map) {
        state.elpReportData = action.payload;
      } else {
        state.elpReportData = convertToMap(action.payload);
      }
      state.elpReportLoading = false;
      state.elpReportError = null;
    },
    updateElpReportData: (state, action) => {
      const { parentId, reportData } = action.payload;
      state.elpReportData.set(parentId, reportData);
    },
    setElpReportLoading: (state, action) => {
      state.elpReportLoading = action.payload;
    },
    setElpReportError: (state, action) => {
      state.elpReportError = action.payload;
      state.elpReportLoading = false;
    },
    clearElpReportData: (state) => {
      state.elpReportData = new Map();
      state.elpReportLoading = false;
      state.elpReportError = null;
    },
    resetTreeState: (state) => {
      state.pageTree = [];
      state.parentTree = [];
      state.selectedParent = null;
    },

    resetTree: (state) => {
      state.pageTree = [];
      state.parentTree = [];
      state.selectedNode = null;
      state.syllabusTree = [];
      state.tempPageTree = [];
      state.tempParentTree = [];
      state.tempSyllabusTree = [];
      state.elpReportData = new Map();
      state.elpReportLoading = false;
      state.elpReportError = null;
    },

    resetTreeExpansion(state) {
      const resetNodes = (nodes: any[]) => {
        return nodes.map((node) => ({
          ...node,
          expanded: false, // reset expanded state
          children: node.children ? resetNodes(node.children) : [],
        }));
      };

      state.pageTree = resetNodes(state.pageTree);
      state.parentTree = resetNodes(state.parentTree);
    },
  },
});

export const {
  setPageTreeData,
  updateSelectedParent,
  addChildrenToPageTree,
  addChildrenToParentTree,
  setParentTreeData,
  collapseExpandInParentTree,
  collapseExpandInPageTree,
  setSelectedNode,
  setTempParentTreeData,
  setTempPageTreeData,
  setSyllabusTreeData,
  collapseExpandInSyllabusTree,
  setTempSyllabusTreeData,
  resetTree,
  setNewSelectedNode,
  setSelectedNodeName,
  setElpReportData,
  updateElpReportData,
  setElpReportLoading,
  setElpReportError,
  clearElpReportData,
  resetTreeState,
  resetTreeExpansion,
} = elpSelectTreeSlice.actions;

export default elpSelectTreeSlice.reducer;
